import { AlertCircle, Check, Copy, Ticket } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Skeleton } from "components/ui/skeleton";

import { describeReadFailure } from "./describe-read-failure";
import { GovernanceCommandFeedback } from "./GovernanceCommandFeedback";
import { useGovernanceCommand } from "./use-governance-command";
import { useGovernanceEvents } from "./use-governance-events";
import { usePlatformInvites } from "./use-platform-invites";
import { APP_LOCALE } from "utils";

const NO_EVENT = "__none__";

/**
 * Every platform invite, and the mint form a super admin uses.
 *
 * The event admin's own version of this lives in the admin area and reads the
 * event-scoped mirror. This reads the whole collection, which the rules open
 * to a super admin alone, and can revoke any invite rather than only its own
 * mints.
 *
 * Naming an event is optional here and mandatory there. A super admin minting
 * without one is issuing a way onto the platform rather than into an event,
 * which is the case the event-scoped channel cannot express.
 */
export function GovernanceInvitesSection({ uid }: { uid: string }) {
  const { state: invites, reload } = usePlatformInvites();
  const mint = useGovernanceCommand();
  const revoke = useGovernanceCommand();

  const busy =
    mint.state.status === "submitting" ||
    mint.state.status === "watching" ||
    revoke.state.status === "submitting" ||
    revoke.state.status === "watching";

  // Reload when the TRIGGER has applied the command, not when submit()
  // resolves: submit returns as soon as the command document is written, which
  // is before the invite exists.
  useEffect(() => {
    if (mint.state.status === "succeeded") reload();
  }, [mint.state.status, reload]);

  useEffect(() => {
    if (revoke.state.status === "succeeded") reload();
  }, [revoke.state.status, reload]);

  return (
    <div className="space-y-6" data-testid="governance-invites-content">
      <MintCard uid={uid} mint={mint} busy={busy} />
      <Card>
        <CardContent className="space-y-3 pt-0">
          <h3 className="text-sm font-semibold text-foreground">All invites</h3>
          <InvitesList
            state={invites}
            busy={busy}
            onRevoke={(inviteId) =>
              void revoke.submit("REVOKE_INVITE", uid, { inviteId })
            }
          />
          <GovernanceCommandFeedback
            state={revoke.state}
            onReset={revoke.reset}
            testId="governance-invite-revoke"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function MintCard({
  uid,
  mint,
  busy,
}: {
  uid: string;
  mint: ReturnType<typeof useGovernanceCommand>;
  busy: boolean;
}) {
  const events = useGovernanceEvents();
  const [label, setLabel] = useState("");
  const [expiryDays, setExpiryDays] = useState("14");
  const [maxUses, setMaxUses] = useState("20");
  const [eventId, setEventId] = useState(NO_EVENT);

  const days = Number(expiryDays);
  const uses = Number(maxUses);
  const canSubmit =
    !busy &&
    Number.isInteger(days) &&
    days > 0 &&
    days <= 365 &&
    Number.isInteger(uses) &&
    uses > 0 &&
    uses <= 500;

  const token = mint.state.status === "succeeded" ? mint.state.mint?.token : "";
  const origin = typeof window === "undefined" ? "" : window.location.origin;

  return (
    <Card>
      <CardContent className="space-y-4 pt-0">
        <h3 className="text-sm font-semibold text-foreground">
          Mint a platform invite
        </h3>
        <div className="space-y-1.5">
          <Label htmlFor="governance-invite-label">Label</Label>
          <Input
            id="governance-invite-label"
            testId="governance-invite-label"
            value={label}
            disabled={busy}
            placeholder="Who is this for?"
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="governance-invite-expiry">Expires in days</Label>
            <Input
              id="governance-invite-expiry"
              testId="governance-invite-expiry"
              type="number"
              min={1}
              max={365}
              value={expiryDays}
              disabled={busy}
              onChange={(e) => setExpiryDays(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="governance-invite-max-uses">Max uses</Label>
            <Input
              id="governance-invite-max-uses"
              testId="governance-invite-max-uses"
              type="number"
              min={1}
              max={500}
              value={maxUses}
              disabled={busy}
              onChange={(e) => setMaxUses(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="governance-invite-event">Event (optional)</Label>
          <Select
            value={eventId}
            onValueChange={setEventId}
            disabled={busy || events.status !== "success"}
          >
            <SelectTrigger
              className="w-full"
              testId="governance-invite-event"
              id="governance-invite-event"
            >
              <SelectValue placeholder="No event" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_EVENT}>No event</SelectItem>
              {events.status === "success" &&
                events.data.map((event) => (
                  <SelectItem key={event.eventId} value={event.eventId}>
                    {event.title ?? event.eventId}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="cta"
          onClick={() => {
            if (!canSubmit) return;
            void mint.submit("MINT_INVITE", uid, {
              label: label.trim(),
              expiresInDays: days,
              maxUses: uses,
              ...(eventId === NO_EVENT ? {} : { contextEventId: eventId }),
            });
          }}
          disabled={!canSubmit}
          data-testid="governance-mint-invite"
        >
          <Ticket className="h-4 w-4" />
          Mint invite
        </Button>
        <ShowMintState
          mint={mint}
          token={token}
          origin={origin}
          onReset={mint.reset}
        />
      </CardContent>
    </Card>
  );
}

/**
 * The token reveal, and why the label says to copy it now: the token cannot be
 * read back from Firestore afterwards, because the invite document holds only
 * its hash. A closed panel means minting again.
 */
function ShowMintState({
  mint,
  token,
  origin,
  onReset,
}: {
  mint: ReturnType<typeof useGovernanceCommand>;
  token: string | undefined;
  origin: string;
  onReset: () => void;
}) {
  if (token) {
    const link = `${origin}/sign-in?invite=${token}`;
    return (
      <div
        className="space-y-2 rounded-lg border border-status-success bg-status-success/15 p-3"
        data-testid="governance-invite-minted"
      >
        <p className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
          Invite minted. Share the sign-in link:
        </p>
        <div className="flex gap-2">
          <Input
            readOnly
            value={link}
            className="font-mono sm:text-xs"
            testId="governance-invite-share-link"
            onFocus={(e) => e.currentTarget.select()}
          />
          <Button
            variant="outline"
            size="sm"
            data-testid="governance-invite-share-link-copy"
            onClick={() => {
              void navigator.clipboard.writeText(link);
              onReset();
            }}
          >
            <Copy className="h-4 w-4" />
            Copy
          </Button>
        </div>
      </div>
    );
  }
  return (
    <GovernanceCommandFeedback
      state={mint.state}
      onReset={onReset}
      testId="governance-invite-mint"
    />
  );
}

function InvitesList({
  state,
  busy,
  onRevoke,
}: {
  state: ReturnType<typeof usePlatformInvites>["state"];
  busy: boolean;
  onRevoke: (inviteId: string) => void;
}) {
  if (state.status === "loading") {
    return (
      <div className="space-y-2" aria-hidden>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <p
        className="flex items-center gap-2 text-sm text-destructive-text"
        data-testid="governance-invites-error"
        role="alert"
      >
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        {describeReadFailure(state.error)}
      </p>
    );
  }
  if (state.invites.length === 0) {
    return <p className="text-sm text-muted-foreground">No invites yet.</p>;
  }

  return (
    <div className="space-y-2">
      {state.invites.map((invite) => {
        const revoked = Boolean(invite.revokedAt);
        const full = invite.issued >= invite.maxUses;
        return (
          <div
            key={invite.id}
            className="flex flex-col gap-2 rounded-lg border border-border px-3 py-2 transition-colors hover:bg-muted/50 sm:flex-row sm:items-center sm:justify-between"
            data-testid="governance-invite-row"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">
                {invite.label ?? "Invite"}
                {revoked && " — revoked"}
              </p>
              <p className="text-xs text-muted-foreground wrap-anywhere">
                {/* The id is sha256 of the token, so a prefix identifies the
                    row without being a credential. */}
                {invite.id.slice(0, 8)} · {invite.issued}/{invite.maxUses} seats
                issued, {invite.uses} signed in
                {invite.contextEventSlug && ` · ${invite.contextEventSlug}`}
                {invite.expiresAt &&
                  ` · until ${formatDate(invite.expiresAt.toDate())}`}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2 max-sm:justify-between">
              {fullOrRevokedBadge(revoked, full)}
              <Button
                variant="destructive"
                size="sm"
                disabled={busy || revoked}
                aria-label={`Revoke ${invite.label ?? "invite"}`}
                data-testid={`governance-revoke-invite-${invite.id}`}
                onClick={() => onRevoke(invite.id)}
              >
                Revoke
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function fullOrRevokedBadge(revoked: boolean, full: boolean) {
  if (revoked)
    return (
      <Badge variant="outline" data-testid="governance-invite-revoked">
        revoked
      </Badge>
    );
  if (full)
    return (
      <Badge variant="outline" data-testid="governance-invite-full">
        full
      </Badge>
    );
  return null;
}

function formatDate(timestamp: Date): string {
  return timestamp.toLocaleDateString(APP_LOCALE, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
